/**
 * accountService.js — Client-side caller for account deletion (LEGAL-07, right
 * to erasure).
 *
 * `deleteMyAccount` returns void; the caller (App.jsx handleDeleteAccount) owns
 * the sign-out / state reset on success.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import logger from "./logger";
import { revokeAppleSignIn } from "./authService";

/**
 * Delete the current user's account (full GDPR right-to-erasure cascade).
 *
 * Calls the `deleteUserAccount` Cloud Function which:
 *   1. Deletes all owned puzzles (top-level + mirror subcollection)
 *   2. Deletes the users/{uid} Firestore document
 *   3. Deletes the Firebase Auth identity
 *
 * The caller (App.jsx handleDeleteAccount) must sign out and reset app state
 * on success — the Firebase Auth user will no longer exist.
 *
 * @returns {Promise<void>}
 * @throws {Error} — if the cascade fails server-side
 */
export async function deleteMyAccount() {
  logger.info("accountService.deleteMyAccount: requesting account erasure");
  // Apple pretende la revoca dei token di Sign in with Apple (#31). Se non
  // riesce (foglio chiuso, rete) la cancellazione va avanti lo stesso: chi ha
  // chiesto di sparire non resta bloccato su un passaggio di Apple.
  try {
    await revokeAppleSignIn();
  } catch (err) {
    logger.warn("accountService.deleteMyAccount: revoca Apple non riuscita", {
      error: err.message,
    });
  }
  try {
    const fn = httpsCallable(functions, "deleteUserAccount");
    await fn();
    logger.info("accountService.deleteMyAccount: erasure complete");
  } catch (err) {
    logger.error("accountService.deleteMyAccount: failed", {
      error: err.message,
      code: err.code,
    });
    throw err;
  }
}
